import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, LogOut, CheckCircle2, PlayCircle, Clock, ArrowLeft, Loader2, AlertTriangle,
  Pause, Volume2, VolumeX, Play, VideoOff, RotateCcw, ClipboardList
} from 'lucide-react';
import toast from 'react-hot-toast';
import trainingApi, { trainingAuth, trainingVideoUrl } from '../services/trainingApi';
import SurveySection from './SurveySection';

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')} min`;
};

const formatVideoTime = (seconds) => {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Kategoria szkolenia z pola audienceType (MANAGERIAL / EMPLOYEE)
const CATEGORY_ORDER = ['Pracownicy', 'Kadra', 'Pozostałe'];

const getCategory = (t) => {
  if (t?.audienceType === 'MANAGERIAL') return 'Kadra';
  if (t?.audienceType === 'EMPLOYEE') return 'Pracownicy';
  const s = `${t?.title || ''}`.toLowerCase();
  if (/kadr/.test(s)) return 'Kadra';
  if (/pracownic|pracown/.test(s)) return 'Pracownicy';
  return 'Pozostałe';
};

// Numer modułu z tytułu (ostatnia liczba), np. "MODUŁ 3" -> 3
const getModuleNumber = (t) => {
  const m = `${t?.title || ''}`.match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : null;
};

const sortTrainings = (list) => {
  return [...(list || [])].sort((a, b) => {
    const ca = CATEGORY_ORDER.indexOf(getCategory(a));
    const cb = CATEGORY_ORDER.indexOf(getCategory(b));
    if (ca !== cb) return ca - cb;
    const na = getModuleNumber(a);
    const nb = getModuleNumber(b);
    if (na != null && nb != null && na !== nb) return na - nb;
    if (na != null && nb == null) return -1;
    if (na == null && nb != null) return 1;
    return (a.id || 0) - (b.id || 0);
  });
};

const ProgressBar = ({ percent, completed }) => (
  <div className="w-full">
    <div className="flex items-center justify-between text-xs mb-1">
      <span className={completed ? 'text-green-600 font-semibold' : 'text-gray-500'}>
        {completed ? 'Ukończono' : percent > 0 ? 'W trakcie' : 'Nierozpoczęte'}
      </span>
      <span className="text-gray-500 font-medium">{percent || 0}%</span>
    </div>
    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${completed ? 'bg-green-500' : 'bg-indigo-500'}`}
        style={{ width: `${Math.min(100, percent || 0)}%` }}
      />
    </div>
  </div>
);

// Miniaturka szkolenia: prawdziwa klatka z filmu + podgląd po najechaniu myszką
const POSTER_TIME = 2.5;

const TrainingThumbnail = ({ training, number }) => {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const progress = training.progress || {};
  const percent = Math.min(100, progress.percentWatched || 0);

  const handleLoadedMetadata = (e) => {
    const v = e.target;
    const dur = v.duration || 0;
    const target = dur > 3 ? POSTER_TIME : Math.max(0.1, dur / 2);
    try { v.currentTime = target; } catch (err) { /* ignore */ }
  };

  const handleEnter = () => {
    if (training.hasVideo && !failed) {
      setLoadVideo(true);
    }
    const v = videoRef.current;
    if (v && training.hasVideo && ready) {
      v.play().catch(() => {});
    }
  };

  const handleLeave = () => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      try { v.currentTime = POSTER_TIME; } catch (e) { /* ignore */ }
    }
  };

  const showVideo = training.hasVideo && !failed && loadVideo;

  return (
    <div
      className="relative aspect-video overflow-hidden bg-slate-900"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {showVideo ? (
        <video
          ref={videoRef}
          src={trainingVideoUrl(training.id)}
          muted
          loop
          playsInline
          preload="none"
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={() => {
            setReady(true);
            videoRef.current?.play().catch(() => {});
          }}
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-blue-600 to-indigo-700 flex items-center justify-center">
          {training.hasVideo ? (
            <PlayCircle className="h-10 w-10 text-white/70" />
          ) : (
            <VideoOff className="h-10 w-10 text-white/70" />
          )}
        </div>
      )}

      {/* Przyciemnienie dla czytelności nakładek */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20 pointer-events-none" />

      {/* Numer kolejny */}
      <span className="absolute top-2 left-2 h-7 min-w-7 px-2 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur text-white text-xs font-bold ring-1 ring-white/20">
        {number}
      </span>

      {/* Status ukończenia */}
      {progress.completed && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-green-600/90 px-2 py-1 rounded-full shadow-sm">
          <CheckCircle2 className="h-3.5 w-3.5" /> Ukończone
        </span>
      )}

      {/* Czas trwania */}
      {training.durationSeconds > 0 && (
        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-medium text-white bg-black/60 backdrop-blur px-2 py-0.5 rounded">
          <Clock className="h-3 w-3" /> {formatDuration(training.durationSeconds)}
        </span>
      )}

      {/* Duży przycisk odtwarzania */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="h-14 w-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-110">
          <Play className="h-6 w-6 text-indigo-700 translate-x-0.5" fill="currentColor" />
        </div>
      </div>

      {/* Cienki pasek postępu na dole miniaturki */}
      {percent > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/25">
          <div
            className={`h-full ${progress.completed ? 'bg-green-400' : 'bg-indigo-400'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
};

const TrainingPlayer = ({ training, onBack, onProgressUpdate }) => {
  const videoRef = useRef(null);
  const lastSentRef = useRef(0);
  const lastTimeRef = useRef(0);
  const resumedRef = useRef(false);
  const isRestoringRef = useRef(false);
  const seekWarnedRef = useRef(false);
  const maxAllowedRef = useRef(training.progress?.maxPositionSeconds || 0);
  const [localProgress, setLocalProgress] = useState(training.progress || { percentWatched: 0, completed: false });
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  const sendProgress = useCallback(async (completed = false) => {
    const video = videoRef.current;
    if (!video) return;
    const position = Math.floor(video.currentTime || 0);
    const duration = Math.floor(video.duration || 0);
    try {
      const res = await trainingApi.post(`/trainings/${training.id}/progress`, {
        position,
        duration,
        completed,
      });
      if (res.data) {
        setLocalProgress(res.data);
        if (onProgressUpdate) onProgressUpdate(training.id, res.data);
      }
    } catch (e) {
      // ciche - heartbeat
    }
  }, [training.id, onProgressUpdate]);

  const enforceMaxTime = useCallback(() => {
    const video = videoRef.current;
    if (!video || isRestoringRef.current) return;
    const max = maxAllowedRef.current;
    if (video.currentTime > max + 0.5) {
      isRestoringRef.current = true;
      video.currentTime = max;
      setCurrentTime(max);
      lastTimeRef.current = max;
      if (!seekWarnedRef.current) {
        seekWarnedRef.current = true;
        toast.error('Przewijanie do przodu jest zablokowane — obejrzyj materiał po kolei');
      }
      setTimeout(() => { isRestoringRef.current = false; }, 100);
    }
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || isRestoringRef.current) return;

    const now = video.currentTime;
    const prev = lastTimeRef.current;
    const max = maxAllowedRef.current;

    // Wykryj nagły skok do przodu (skróty klawiaturowe, manipulacja DOM)
    if (prev > 0 && now - prev > 2 && now > max + 0.5) {
      enforceMaxTime();
      return;
    }

    // Normalne odtwarzanie — rozszerz dozwolony zakres tylko płynnie
    if (!video.paused && now > max && now <= prev + 2.5) {
      maxAllowedRef.current = now;
    }

    lastTimeRef.current = now;
    setCurrentTime(now);

    const tick = Math.floor(now);
    if (tick - lastSentRef.current >= 10) {
      lastSentRef.current = tick;
      sendProgress(false);
    }
  };

  const handleSeeking = () => enforceMaxTime();
  const handleSeeked = () => enforceMaxTime();

  const blockSeekKeys = (e) => {
    if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown', 'j', 'l', 'k'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || resumedRef.current) return;
    resumedRef.current = true;
    setDuration(video.duration || 0);
    const resumeAt = training.progress?.maxPositionSeconds || 0;
    maxAllowedRef.current = Math.max(maxAllowedRef.current, resumeAt);
    lastTimeRef.current = resumeAt;
    if (resumeAt > 5 && video.duration && resumeAt < video.duration - 5) {
      try {
        video.currentTime = resumeAt;
        setCurrentTime(resumeAt);
      } catch (e) { /* ignore */ }
    }
  };

  useEffect(() => {
    const guard = setInterval(() => enforceMaxTime(), 400);
    return () => clearInterval(guard);
  }, [enforceMaxTime]);

  useEffect(() => {
    return () => {
      // wyślij ostatni postęp przy zamykaniu odtwarzacza
      const video = videoRef.current;
      if (video && video.currentTime > 0) {
        const position = Math.floor(video.currentTime);
        const duration = Math.floor(video.duration || 0);
        try {
          navigator.sendBeacon &&
            trainingApi.post(`/trainings/${training.id}/progress`, { position, duration, completed: false });
        } catch (e) { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Powrót do listy szkoleń
      </button>

      <div
        className="bg-black rounded-xl overflow-hidden shadow-lg"
        onKeyDown={blockSeekKeys}
        tabIndex={0}
      >
        <video
          ref={videoRef}
          src={trainingVideoUrl(training.id)}
          playsInline
          disablePictureInPicture
          controlsList="nodownload noremoteplayback nofullscreen"
          className="w-full max-h-[70vh] bg-black block"
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onSeeked={handleSeeked}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setPlaying(true)}
          onPause={() => { setPlaying(false); sendProgress(false); }}
          onEnded={() => sendProgress(true)}
          onContextMenu={(e) => e.preventDefault()}
        >
          Twoja przeglądarka nie obsługuje odtwarzania wideo.
        </video>

        {/* Własne sterowanie — bez paska przewijania (brak możliwości skip) */}
        <div className="bg-gray-900 px-4 py-3 flex items-center gap-4">
          <button
            type="button"
            onClick={togglePlay}
            className="text-white hover:text-indigo-300 transition-colors"
            aria-label={playing ? 'Pauza' : 'Odtwórz'}
          >
            {playing ? <Pause className="h-6 w-6" /> : <PlayCircle className="h-6 w-6" />}
          </button>

          <span className="text-white text-sm font-mono tabular-nums shrink-0">
            {formatVideoTime(currentTime)} / {formatVideoTime(duration)}
          </span>

          {/* Pasek tylko do podglądu — nie da się klikać */}
          <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden pointer-events-none select-none">
            <div
              className="h-full bg-indigo-500 rounded-full transition-[width] duration-300"
              style={{ width: duration ? `${Math.min(100, (currentTime / duration) * 100)}%` : '0%' }}
            />
          </div>

          <button
            type="button"
            onClick={toggleMute}
            className="text-white hover:text-indigo-300 transition-colors shrink-0"
            aria-label={muted ? 'Włącz dźwięk' : 'Wycisz'}
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div className="mt-5 bg-white rounded-xl shadow-sm ring-1 ring-black/5 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{training.title}</h2>
            {training.required && (
              <span className="inline-block mt-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                Szkolenie obowiązkowe
              </span>
            )}
          </div>
          {localProgress.completed && (
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-green-600">
              <CheckCircle2 className="h-5 w-5" /> Ukończone
            </span>
          )}
        </div>
        {training.description && (
          <p className="mt-3 text-sm text-gray-600 whitespace-pre-line">{training.description}</p>
        )}
        <div className="mt-4 max-w-md">
          <ProgressBar percent={localProgress.percentWatched} completed={localProgress.completed} />
        </div>
        <p className="mt-3 text-xs text-gray-400">
          Postęp zapisywany jest automatycznie. Szkolenie uznaje się za zaliczone po obejrzeniu co najmniej 90% materiału.
          Przewijanie do przodu jest zablokowane — możesz wstrzymać i wrócić do obejrzanego fragmentu.
        </p>
      </div>
    </div>
  );
};

const TrainingPortal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(trainingAuth.getUser());
  const [mainTab, setMainTab] = useState('trainings'); // trainings | surveys
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const loadTrainings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await trainingApi.get('/trainings');
      // Kolejność: wg kategorii, potem wg numeru modułu (1, 2, 3…)
      setTrainings(sortTrainings(res.data));
    } catch (e) {
      if (e.response?.status === 401) {
        navigate('/login?portal=szkolenia');
        return;
      }
      toast.error('Nie udało się pobrać listy szkoleń');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!trainingAuth.getToken()) {
      navigate('/login?portal=szkolenia');
      return;
    }
    // odśwież dane użytkownika
    trainingApi.get('/me')
      .then((res) => setUser(res.data))
      .catch(() => {});
    loadTrainings();
  }, [navigate, loadTrainings]);

  const handleLogout = () => {
    trainingAuth.logout();
    navigate('/login?portal=szkolenia');
  };

  const handleProgressUpdate = (trainingId, progress) => {
    setTrainings((prev) => prev.map((t) => (t.id === trainingId ? { ...t, progress } : t)));
    setSelected((prev) => (prev && prev.id === trainingId ? { ...prev, progress } : prev));
  };

  const completedCount = trainings.filter((t) => t.progress?.completed).length;
  const requiredPending = trainings.filter((t) => t.required && !t.progress?.completed).length;

  // Kategorie obecne w danych (w ustalonej kolejności)
  const categories = CATEGORY_ORDER.filter((c) => trainings.some((t) => getCategory(t) === c));
  const showCategoryTabs = categories.length > 1;
  const visibleTrainings = activeCategory === 'all'
    ? trainings
    : trainings.filter((t) => getCategory(t) === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      {/* Pasek górny */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 leading-tight">Portal szkoleń / ankiet</p>
              <p className="text-xs text-gray-500 leading-tight">CRM LAPY · Szpital Łapy</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                {user?.displayName || user?.username}
              </p>
              <p className="text-xs text-gray-400 leading-tight">konto domenowe</p>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Wyloguj
            </button>
          </div>
        </div>
        {!selected && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
            <div className="inline-flex gap-1 p-1 bg-gray-100 rounded-xl">
              <button
                type="button"
                onClick={() => setMainTab('trainings')}
                className={`inline-flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                  mainTab === 'trainings'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <GraduationCap className="h-4 w-4" />
                Szkolenia
              </button>
              <button
                type="button"
                onClick={() => setMainTab('surveys')}
                className={`inline-flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
                  mainTab === 'surveys'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <ClipboardList className="h-4 w-4" />
                Ankiety
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {mainTab === 'surveys' && !selected ? (
          <SurveySection />
        ) : selected ? (
          <TrainingPlayer
            training={selected}
            onBack={() => { setSelected(null); loadTrainings(); }}
            onProgressUpdate={handleProgressUpdate}
          />
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Twoje szkolenia</h1>
              <p className="text-sm text-gray-500 mt-1">
                Obejrzyj poniższe materiały szkoleniowe. Twój postęp jest zapisywany automatycznie.
              </p>
            </div>

            {/* Podsumowanie */}
            {!loading && trainings.length > 0 && (
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
                <div className="bg-white rounded-2xl p-4 ring-1 ring-black/5 flex items-center gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-gray-900 leading-none">{trainings.length}</p>
                    <p className="text-xs text-gray-500 mt-1">dostępnych szkoleń</p>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-4 ring-1 ring-black/5 flex items-center gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-green-50 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-green-600 leading-none">{completedCount}</p>
                    <p className="text-xs text-gray-500 mt-1">ukończonych</p>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-4 ring-1 ring-black/5 flex items-center gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-xl bg-amber-50 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-amber-600 leading-none">{requiredPending}</p>
                    <p className="text-xs text-gray-500 mt-1">obowiązkowych do zrobienia</p>
                  </div>
                </div>
              </div>
            )}

            {/* Filtr kategorii */}
            {!loading && showCategoryTabs && (
              <div className="flex flex-wrap gap-2 mb-5">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    activeCategory === 'all'
                      ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/20'
                      : 'bg-white text-gray-600 ring-1 ring-black/5 hover:bg-gray-50'
                  }`}
                >
                  Wszystkie
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setActiveCategory(c)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                      activeCategory === c
                        ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/20'
                        : 'bg-white text-gray-600 ring-1 ring-black/5 hover:bg-gray-50'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <Loader2 className="h-6 w-6 animate-spin mr-2" /> Ładowanie szkoleń…
              </div>
            ) : trainings.length === 0 ? (
              <div className="bg-white rounded-xl ring-1 ring-black/5 p-10 text-center text-gray-500">
                <GraduationCap className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                Brak dostępnych szkoleń. Zajrzyj tu później.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {visibleTrainings.map((t, index) => {
                  const progress = t.progress || {};
                  const clickable = t.hasVideo;
                  const number = getModuleNumber(t) || (index + 1);
                  return (
                    <div
                      key={t.id}
                      onClick={() => clickable && setSelected(t)}
                      className={`group bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden flex flex-col transition-all duration-200 ${
                        clickable
                          ? 'cursor-pointer hover:shadow-xl hover:ring-indigo-200 hover:-translate-y-0.5'
                          : 'opacity-90'
                      }`}
                    >
                      <TrainingThumbnail training={t} number={number} />

                      <div className="p-4 flex flex-col flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-gray-900 leading-snug flex-1">{t.title}</h3>
                          {t.required && (
                            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded-md">
                              Obowiązkowe
                            </span>
                          )}
                        </div>

                        {t.description && (
                          <p className="mt-1.5 text-xs text-gray-500 line-clamp-2">{t.description}</p>
                        )}

                        <div className="mt-4">
                          <ProgressBar percent={progress.percentWatched} completed={progress.completed} />
                        </div>

                        <button
                          onClick={(e) => { e.stopPropagation(); setSelected(t); }}
                          disabled={!t.hasVideo}
                          className={`mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-colors ${
                            !t.hasVideo
                              ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                              : progress.completed
                                ? 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100'
                                : 'text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm shadow-indigo-600/20'
                          }`}
                        >
                          {!t.hasVideo ? (
                            <><AlertTriangle className="h-4 w-4" /> Film niedostępny</>
                          ) : progress.completed ? (
                            <><RotateCcw className="h-4 w-4" /> Obejrzyj ponownie</>
                          ) : progress.percentWatched > 0 ? (
                            <><PlayCircle className="h-4 w-4" /> Kontynuuj</>
                          ) : (
                            <><Play className="h-4 w-4" fill="currentColor" /> Rozpocznij</>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default TrainingPortal;
